import React, { useEffect, useState } from "react";
import { Castle, RefreshCcw } from "lucide-react";
import { LayoutGridDemo } from "../../animation/LayoutGridDemo";
import { useFunctionContext } from "../../context/FunctionalContext";
import ListingCard from "./listings/ListingCard";
import DottedLoading from "../../common/DottedLoading";

import ListYourProperty from "./listings/ListYourProperty";
import { useLocation } from "react-router-dom";

const Listings = () => {
  const [rotating, setRotating] = useState(false);
  const { listLoading, listHasMore, fetchListings, listings } =
    useFunctionContext();

  // const handleLoadMore = () => {
  //   if (listLoading || !listHasMore) return;

  //   setRotating(true);
  //   fetchListings(listings.length, true).finally(() => {
  //     setRotating(false);
  //     setTimeout(() => {
  //       window.scrollBy({ top: 400, behavior: "smooth" });
  //     }, 300);
  //   });
  // };

  const location = useLocation();

  const handleRefresh = () => {
    setRotating(true);
    fetchListings(0, true).finally(() => {
      setRotating(false);
    });
  };

  // Step 1: Group listings by listing_type
  const groupedListings = listings.reduce((acc, item) => {
    const type = item.listing_type || "Others";
    if (!acc[type]) acc[type] = [];
    acc[type].push(item);
    return acc;
  }, {});

  // Step 2: Map backend types to your section titles
  const typeMapping = {
    Rent: "Properties On Rent",
    Sale: "Properties On Sale",
    "Hot Listing": "Hot Listings",
    Offplan: "Off Plan Properties",
    "Ready To Move": "Ready To Move",
    "Sold/Rented": "Sold/Rented",
    "Investor Friendly Project": "Investor Friendly Project",
  };

  // Step 3: Define the order of sections
  const sections = [
    "Properties On Rent",
    "Properties On Sale",
    "Hot Listings",
    "Off Plan Properties",
    "Ready To Move",
    "Sold/Rented",
    "Investor Friendly Project",
  ];

  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace("#", "");
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 50); // slight delay to ensure DOM is ready
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location]);

  return (
    <>
      <section className="relative h-[70vh] md:h-[85vh] flex items-center justify-center overflow-hidden bg-gradient-to-r from-gold/10 to-black text-white py-10 sm:py-16 lg:pt-36 lg:pb-24 ">
        <div
          className="absolute inset-0 bg-cover bg-center bg-fixed lg:bg-fixed bg-scroll z-0"
          style={{
            backgroundImage: 'url("/images/realty/15.png")',
            // backgroundAttachment: "fixed",
            backgroundSize: "cover",
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        </div>
        <div className="relative z-10 text-center px-4 md:px-10 animate-fade-in mt-14 lg:mt-0">
          <Castle className="md:w-16 md:h-16 w-12 h-12 text-white mx-auto mb-6" />
          <h1 className="text-3xl md:text-6xl font-bold text-white mb-4">
            Invest in Dubai's Future
          </h1>
          <p className="text-base md:text-xl text-gray-200 max-w-3xl mx-auto">
            Discover exclusive investment opportunities and navigate Dubai’s
            dynamic property landscape with our expert guidance, tailored to
            maximize your investment potential.
          </p>
        </div>
      </section>

      <LayoutGridDemo />

      <section className="py-10 lg:pt-20 lg:pb-32 md:py-16 container">
        <div className="mx-auto sm:px-6 lg:px-8 max-w-7xl flex flex-col">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold leading-tight md:text-3xl lg:text-5xl">
              Latest Listings
            </h2>
            <p className="max-w-xl mx-auto mt-4 text-lg leading-relaxed text-gray-400">
              Stay updated with our most recent Real-estate and insights.
            </p>
          </div>

          <div className="float-end ms-auto inline-flex gap-5 items-center mt-8">
            {listings?.length > 0 ? (
              <div
                className="relative group inline-flex gap-1 justify-end cursor-pointer"
                onClick={!rotating ? handleRefresh : null}
              >
                <RefreshCcw className={rotating ? "animate-spin" : ""} />
                {/* Tooltip */}
                <span className="absolute bottom-full mb-2 px-2 py-1 text-xs font-semibold text-white bg-slate-800 rounded opacity-0 group-hover:opacity-100 transition text-nowrap">
                  Refresh for latest
                </span>
              </div>
            ) : null}
            {/* <button className="bg-white text-black btn !py-1 !rounded-lg">
              List Your Property
            </button> */}
            <ListYourProperty />
          </div>

          <div className="space-y-16">
            {listLoading ? (
              // Skeleton Loader
              <div className="grid grid-cols-1 mt-10 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-x-8 lg:gap-y-12">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="w-[340px] lg:w-[336px] rounded-xl bg-black p-3 animate-pulse"
                  >
                    <div className="relative w-full h-[180px] bg-gray-800 rounded-lg" />
                    <div className="mt-4 space-y-2">
                      <div className="h-4 bg-gray-700 rounded w-4/5"></div>
                      <div className="h-4 bg-gray-700 rounded w-3/5"></div>
                    </div>
                    <div className="mt-4 flex items-center justify-between text-sm">
                      <div className="h-4 w-20 bg-gray-700 rounded"></div>
                      <div className="h-4 w-16 bg-gray-700 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : !listings || listings.length === 0 ? (
              <section className="py-20 text-center">
                {" "}
                <div className="container max-w-3xl mx-auto px-4">
                  {" "}
                  <h2 className="text-3xl md:text-4xl font-bold text-gold mb-4">
                    {" "}
                    Coming Soon{" "}
                  </h2>{" "}
                  <p className="text-gray-400 text-lg">
                    {" "}
                    Our listings are being updated. Please check back soon or
                    contact our team for exclusive previews.{" "}
                  </p>{" "}
                </div>{" "}
              </section>
            ) : (
              sections.map((section) => {
                // Find backend key for this section
                const backendType = Object.keys(typeMapping).find(
                  (key) => typeMapping[key] === section
                );

                const hasListings =
                  backendType && groupedListings[backendType]?.length > 0;

                return (
                  <div
                    id={section.toLowerCase().replace(/\s+/g, "-")} // ✅ Add this line
                    key={section}
                    className="mt-14 scroll-mt-32"
                  >
                    {/* Section Title */}
                    <h2 className="text-xl lg:text-3xl font-bold mb-6 underline">
                      {section}:
                    </h2>

                    {/* If section has listings */}
                    {hasListings ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-x-8 lg:gap-y-12">
                        {groupedListings[backendType].map((list) => (
                          <ListingCard key={list.listing_id} list={list} />
                        ))}
                      </div>
                    ) : (
                      // Coming Soon section
                      <div className="py-20 text-center w-full">
                        <h3 className="text-2xl font-semibold mb-2 bg-gradient-to-r from-gold via-slate-500 to-gold bg-[length:200%_auto] text-transparent bg-clip-text animate-gradient">
                          Coming Soon ⭐
                        </h3>
                        <p className="text-gray-400 text-sm">
                          Our listings are being updated. Please check back soon
                          or contact our team for exclusive previews.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* <div className="grid grid-cols-1 mt-10 md:grid-cols-2 lg:mt-16 lg:grid-cols-3 gap-4 lg:gap-x-8 lg:gap-y-12">
            {listLoading ? (
              Array.from({ length: listings.length || 3 }).map((_, index) => (
                <div
                  key={index}
                  className="relative transition-all duration-300 transform"
                >
                  <div className="w-[276px] lg:w-[336px] rounded-xl bg-black p-3 animate-pulse">
                    <div className="relative w-full h-[180px] bg-gray-800 rounded-lg">
                      <div className="absolute top-2 left-2 h-6 w-14 bg-gray-700 rounded-full"></div>
                    </div>

                    <div className="mt-4 space-y-2">
                      <div className="h-4 bg-gray-700 rounded w-4/5"></div>
                      <div className="h-4 bg-gray-700 rounded w-3/5"></div>
                    </div>

                    <div className="mt-4 flex items-center justify-between text-sm">
                      <div className="h-4 w-20 bg-gray-700 rounded"></div>
                      <div className="h-4 w-16 bg-gray-700 rounded"></div>
                    </div>
                  </div>
                </div>
              ))
            ) : !listings || listings.length === 0 ? (
              <div className="text-center text-gray-400 py-10 w-full col-span-full">
                🚫 No listings available.
              </div>
            ) : (
              listings.map((list) => (
                <ListingCard key={list.listing_id} list={list} />
              ))
            )}
          </div> */}

          {/* {listHasMore && !listLoading && (
            <div className="flex justify-center mt-10">
              <button
                onClick={handleLoadMore}
                className="bg-white text-black px-5 py-1.5 rounded transition-all duration-300 hover:bg-white/85 disabled:opacity-70"
              >
                Load More
              </button>
            </div>
          )}

          {listLoading && (
            <div className="flex justify-center mt-10">
              <DottedLoading text="Loading listings..." />
            </div>
          )} */}
        </div>
      </section>
    </>
  );
};

export default Listings;
